import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  DEADLINE_RECURRENCE_CHOICES,
  type DeadlineRecurrenceChoice,
  type DeadlineValiditySuggestionDto,
  MAX_RENEWAL_VALIDITY_MONTHS,
} from '@prospero/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraftDateOnlyValue } from '../../hooks/use-draft-date-only-value';
import { applyRecurrenceChoice, type DeadlineRecurrenceValue } from './deadline-recurrence';

export interface DeadlineRecurrenceFieldsProps {
  value: DeadlineRecurrenceValue;
  onChange: (next: DeadlineRecurrenceValue) => void;
  /** Risposte mostrate; default tutte e tre. La tranche 8 passa ['NONE', 'RECURRING']. */
  availableChoices?: readonly DeadlineRecurrenceChoice[];
  /** Template studio (tranche 8): nasconde "Data di scadenza" e "Prossima scadenza". */
  hideDates?: boolean;
  /** Suggerimento già risolto dal chiamante: il componente non chiama il server. */
  suggestion?: DeadlineValiditySuggestionDto;
  isSuggestionLoading?: boolean;
  /** Tipo a durata regionale (warningOnly): aggiunge l'avviso HACCP sotto "Valido per". */
  showRegionalHint?: boolean;
  /** Obbligatoria anche con `hideDates`: `getLocalTodayIso()`. */
  todayIso: string;
  /** Errori del form (React Hook Form) per campo. */
  errors?: Partial<Record<keyof DeadlineRecurrenceValue, boolean>>;
  disabled?: boolean;
  /** `data-testid`: `<prefix>-choice-<CHOICE>`, `<prefix>-validity`, `<prefix>-due-at`. */
  testIdPrefix: string;
}

const TOUCH_TARGET_PX = 48;
const INPUT_FONT_SIZE_PX = 16;

function parseMonths(raw: string): number | undefined {
  const months = Number.parseInt(raw, 10);
  return Number.isNaN(months) ? undefined : months;
}

interface RecurrenceChoiceOptionProps {
  choice: DeadlineRecurrenceChoice;
  helperId: string;
  testId: string;
}

function RecurrenceChoiceOption({ choice, helperId, testId }: RecurrenceChoiceOptionProps) {
  const { t } = useTranslation('documents');
  return (
    <Stack>
      <FormControlLabel
        value={choice}
        data-testid={testId}
        sx={{ minHeight: TOUCH_TARGET_PX, mr: 0 }}
        control={<Radio sx={{ p: 1.5 }} slotProps={{ input: { 'aria-describedby': helperId } }} />}
        label={t(`deadlines.recurrence.choice.${choice}`)}
      />
      <Typography id={helperId} variant="caption" color="text.secondary" sx={{ pl: 5.5, mt: -0.5 }}>
        {t(`deadlines.recurrence.choiceHelper.${choice}`)}
      </Typography>
    </Stack>
  );
}

function RecurrenceChoiceGroup(props: DeadlineRecurrenceFieldsProps) {
  const { t } = useTranslation('documents');
  const { value, onChange, testIdPrefix, suggestion, errors, disabled } = props;
  const availableChoices = props.availableChoices ?? DEADLINE_RECURRENCE_CHOICES;
  const legendId = `${testIdPrefix}-legend`;
  const choose = (next: DeadlineRecurrenceChoice) =>
    onChange(applyRecurrenceChoice(value, next, suggestion?.validityMonths ?? null));

  return (
    <FormControl error={errors?.recurrence} disabled={disabled}>
      <FormLabel id={legendId}>{t('deadlines.recurrence.legend')}</FormLabel>
      <RadioGroup
        aria-labelledby={legendId}
        value={value.recurrence ?? ''}
        onChange={(event) => choose(event.target.value as DeadlineRecurrenceChoice)}
      >
        {availableChoices.map((choice) => (
          <RecurrenceChoiceOption
            key={choice}
            choice={choice}
            helperId={`${testIdPrefix}-helper-${choice}`}
            testId={`${testIdPrefix}-choice-${choice}`}
          />
        ))}
      </RadioGroup>
      {errors?.recurrence && (
        <FormHelperText>{t('deadlines.recurrence.choiceRequired')}</FormHelperText>
      )}
    </FormControl>
  );
}

function resolveValidityHelperKey(
  props: DeadlineRecurrenceFieldsProps,
  showError: boolean,
): string {
  if (showError) {
    return 'deadlines.recurrence.validityError';
  }
  if (props.isSuggestionLoading) {
    return 'deadlines.recurrence.suggestion.loading';
  }
  if (props.suggestion?.source) {
    return `deadlines.recurrence.suggestion.${props.suggestion.source}`;
  }
  return 'deadlines.recurrence.validityRange';
}

function ValidityMonthsField(props: DeadlineRecurrenceFieldsProps) {
  const { t } = useTranslation('documents');
  const { value, onChange, disabled, errors, testIdPrefix } = props;
  const [isTouched, setIsTouched] = useState(false);
  const showError = errors?.validityMonths === true && isTouched;

  return (
    <Stack spacing={1}>
      <TextField
        type="number"
        label={t('deadlines.recurrence.validityLabel')}
        value={value.validityMonths ?? ''}
        onChange={(event) => {
          setIsTouched(true);
          onChange({ ...value, validityMonths: parseMonths(event.target.value) });
        }}
        onBlur={() => setIsTouched(true)}
        disabled={disabled}
        error={showError}
        helperText={t(resolveValidityHelperKey(props, showError))}
        fullWidth
        sx={{ '& input': { fontSize: INPUT_FONT_SIZE_PX } }}
        slotProps={{
          htmlInput: {
            min: 1,
            max: MAX_RENEWAL_VALIDITY_MONTHS,
            inputMode: 'numeric',
            'data-testid': `${testIdPrefix}-validity`,
          },
        }}
      />
      {props.showRegionalHint && (
        <Typography variant="caption" color="text.secondary">
          {t('deadlines.renew.validityHaccpHelper')}
        </Typography>
      )}
    </Stack>
  );
}

function resolveDueAtHelper(
  t: (key: string) => string,
  state: { showError: boolean; isInPast: boolean },
): string | undefined {
  if (state.showError) {
    return t('deadlines.recurrence.dueAtError');
  }
  return state.isInPast ? t('deadlines.recurrence.pastDateHelper') : undefined;
}

function RecurrenceDueAtField(props: DeadlineRecurrenceFieldsProps) {
  const { t } = useTranslation('documents');
  const { value, onChange, disabled, errors, testIdPrefix, todayIso } = props;
  const [isTouched, setIsTouched] = useState(false);
  const labelKey =
    value.recurrence === 'RECURRING'
      ? 'deadlines.recurrence.nextDueAtLabel'
      : 'deadlines.recurrence.dueAtLabel';
  const isInPast = value.dueAt !== undefined && value.dueAt < todayIso;
  const showError = errors?.dueAt === true && isTouched;
  const { pickerValue, handlePickerChange } = useDraftDateOnlyValue(value.dueAt ?? '', (iso) => {
    setIsTouched(true);
    onChange({ ...value, dueAt: iso === '' ? undefined : iso });
  });

  return (
    <Box data-testid={`${testIdPrefix}-due-at`}>
      <DatePicker
        label={t(labelKey)}
        value={pickerValue}
        onChange={handlePickerChange}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            error: showError,
            onBlur: () => setIsTouched(true),
            helperText: resolveDueAtHelper(t, { showError, isInPast }),
            name: 'deadline-due-at',
            sx: { '& input': { fontSize: INPUT_FONT_SIZE_PX } },
            slotProps: {
              htmlInput: { autoComplete: 'off' },
            },
          },
          actionBar: { actions: ['accept'] },
        }}
      />
    </Box>
  );
}

export function DeadlineRecurrenceFields(props: DeadlineRecurrenceFieldsProps) {
  const { value, hideDates = false } = props;
  const hasDateAnswer = value.recurrence === 'ONE_OFF' || value.recurrence === 'RECURRING';

  return (
    <Stack spacing={2}>
      <RecurrenceChoiceGroup {...props} />
      {value.recurrence === 'RECURRING' && <ValidityMonthsField {...props} />}
      {!hideDates && hasDateAnswer && <RecurrenceDueAtField {...props} />}
    </Stack>
  );
}
